import { useEffect, useMemo, useState } from "preact/hooks";
import qrcodegen from "../../vendor/qrcodegen";
import type { CoreData } from "../data/types";
import { useStore } from "../state/store";
import { DEFAULT_FILTERS, ask, decisions, filters, listByVerdict, onPhone, settings, shareOrCopy, siteOrigin, toast } from "../state/app";
import {
  createRoom,
  joinRoom,
  leaveRoom,
  matchesOf,
  pageAccess,
  partner,
  partnerSets,
  pull,
  restoreCode,
} from "../state/partner";
import { clearPendingJoin, goTab, openName, peekPendingJoin } from "../state/router";
import { fmt, initialsOf } from "../data/format";
import { entryFor, loadDetail } from "../data/load";
import { swipeWith } from "../state/deck";
import { likeList } from "../state/taste";
import { NameRow } from "./Shortlist";
import { IconCopy, IconHeart, IconLink, IconRefresh, IconShare, IconStar, IconTwoHearts } from "./icons";

export function Qr({ text, size = 184, label }: { text: string; size?: number; label: string }) {
  const path = useMemo(() => {
    const qr = qrcodegen.QrCode.encodeText(text, qrcodegen.QrCode.Ecc.MEDIUM);
    let d = "";
    for (let y = 0; y < qr.size; y++) for (let x = 0; x < qr.size; x++) if (qr.getModule(x, y)) d += `M${x + 3},${y + 3}h1v1h-1z`;
    return { d, n: qr.size + 6 };
  }, [text]);
  return (
    <svg class="qr" viewBox={`0 0 ${path.n} ${path.n}`} width={size} height={size} role="img" aria-label={label} shape-rendering="crispEdges">
      <rect width={path.n} height={path.n} fill="#fff" rx="2" />
      <path d={path.d} fill="#1b1512" />
    </svg>
  );
}

function ago(ts: number): string {
  if (!ts) return "not yet";
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  return `${h} hour${h === 1 ? "" : "s"} ago`;
}

function useTick(ms: number): void {
  const [, set] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => set((x) => x + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label class="toggle-row">
      <span>
        <span class="toggle-label">{label}</span>
        {hint && <span class="toggle-hint">{hint}</span>}
      </span>
      <input type="checkbox" role="switch" checked={checked} onChange={(ev) => onChange((ev.target as HTMLInputElement).checked)} />
      <span class="switch" aria-hidden="true" />
    </label>
  );
}

function NotPaired() {
  const myName = useStore(settings, (s) => s.myName);
  const status = useStore(partner, (s) => s.status);
  const error = useStore(partner, (s) => s.error);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    const pending = peekPendingJoin();
    if (pending) {
      setCode(pending);
      setJoining(true);
    }
  }, []);

  const busy = status === "syncing";
  const needName = !myName.trim();

  return (
    <div class="together-intro">
      <div class="hero-hearts" aria-hidden="true">
        <IconTwoHearts size={64} />
      </div>
      <h1 class="view-title center">Choose together</h1>
      <p class="view-sub center">
        You each swipe on your own {onPhone() ? "phone" : "device"}. Nameling quietly compares notes and only shows you the names you <em>both</em>{" "}
        like.
      </p>
      <ul class="howto">
        <li>
          <b>1</b> One of you creates a pair code
        </li>
        <li>
          <b>2</b> The other scans it or types it in
        </li>
        <li>
          <b>3</b> Swipe away. Matches pop up as they happen
        </li>
      </ul>

      <label class="field">
        <span>Your first name</span>
        <input
          type="text"
          value={myName}
          maxLength={40}
          autocomplete="given-name"
          placeholder="So your partner knows it's you"
          onInput={(ev) => settings.set({ myName: (ev.target as HTMLInputElement).value })}
        />
      </label>

      {!joining ? (
        <div class="stack">
          <button
            class="btn btn-primary btn-wide"
            disabled={busy || needName}
            onClick={() => createRoom().catch(() => {})}
          >
            {busy ? "Creating…" : "Create a pair code"}
          </button>
          <button class="btn btn-wide" onClick={() => setJoining(true)}>
            I've got a pair code
          </button>
        </div>
      ) : (
        <form
          class="stack"
          onSubmit={(ev) => {
            ev.preventDefault();
            joinRoom(code).then(clearPendingJoin, () => {});
          }}
        >
          <label class="field">
            <span>Pair code</span>
            <input
              class="code-input"
              type="text"
              inputMode="text"
              autocapitalize="characters"
              autocomplete="one-time-code"
              maxLength={9}
              value={code}
              placeholder="ABCD 2345"
              onInput={(ev) => setCode((ev.target as HTMLInputElement).value.toUpperCase())}
            />
          </label>
          <button class="btn btn-primary btn-wide" disabled={busy || needName || ![6, 8].includes(code.replace(/[^A-Z0-9]/gi, "").length)}>
            {busy ? "Joining…" : "Join"}
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-wide"
            onClick={() => {
              clearPendingJoin();
              setJoining(false);
            }}
          >
            Back
          </button>
        </form>
      )}
      {needName && <p class="muted small center">Add your first name to pair up.</p>}
      {error && status === "error" && (
        <p class="error-text" role="alert">
          {error}
        </p>
      )}
      <p class="privacy-note">
        Only names you both like are revealed. Nobody needs an account or an email address.
      </p>
    </div>
  );
}

/** claude.ai preview: the page itself is the room, so joining in replaces pair codes. */
function PageNotPaired() {
  const myName = useStore(settings, (s) => s.myName);
  const status = useStore(partner, (s) => s.status);
  const error = useStore(partner, (s) => s.error);
  const access = useStore(pageAccess, (s) => s.access);
  const busy = status === "syncing";
  const needName = !myName.trim();
  const blocked = access !== "ready";

  return (
    <div class="together-intro">
      <div class="hero-hearts" aria-hidden="true">
        <IconTwoHearts size={64} />
      </div>
      <h1 class="view-title center">Choose together</h1>
      <p class="view-sub center">
        You each swipe on your own device. Nameling quietly compares notes and only shows you the names you <em>both</em>{" "}
        like.
      </p>
      <ul class="howto">
        <li>
          <b>1</b> Share this page with your partner by email, as an editor
        </li>
        <li>
          <b>2</b> You each open it signed in to Claude and tap “Join in”
        </li>
        <li>
          <b>3</b> Swipe away. Matches pop up as they happen
        </li>
      </ul>

      <label class="field">
        <span>Your first name</span>
        <input
          type="text"
          value={myName}
          maxLength={40}
          autocomplete="given-name"
          placeholder="So your partner knows it's you"
          onInput={(ev) => settings.set({ myName: (ev.target as HTMLInputElement).value })}
        />
      </label>

      {access === "unavailable" && (
        <p class="preview-note" role="note">
          Matching works when this page is opened on claude.ai while signed in. You can still swipe and build a shortlist
          here.
        </p>
      )}
      {access === "readonly" && (
        <p class="preview-note" role="note">
          You can see this page but not save to it, so your swipes can't be matched yet. Ask whoever shared it to invite you
          by email as an editor.
        </p>
      )}
      <div class="stack">
        <button class="btn btn-primary btn-wide" disabled={busy || needName || blocked} onClick={() => createRoom().catch(() => {})}>
          {busy ? "Joining…" : access === "checking" ? "Checking…" : "Join in"}
        </button>
      </div>
      {needName && !blocked && <p class="muted small center">Add your first name to join in.</p>}
      {error && status === "error" && (
        <p class="error-text" role="alert">
          {error}
        </p>
      )}
      <p class="privacy-note">
        Only names you both like are shown. Your swipes are saved with this page so the people you share it with can match
        with you, and you can leave at any time.
      </p>
    </div>
  );
}

function PageWaiting() {
  return (
    <div class="waiting">
      <div class="hero-hearts" aria-hidden="true">
        <IconTwoHearts size={64} />
      </div>
      <h1 class="view-title center">You're paired</h1>
      <p class="view-sub center">Now your partner needs to join in too.</p>
      <ul class="howto">
        <li>
          <b>1</b> On claude.ai, open this page's Share menu and invite them by email as an editor
        </li>
        <li>
          <b>2</b> They open the page signed in, go to Together and tap “Join in”
        </li>
      </ul>
      <p class="muted small center">
        People who can only view the page can't save their swipes. If you also share it by public link, people outside your
        organisation can only view it.
      </p>
      <p class="waiting-status">
        <span class="pulse" aria-hidden="true" /> Waiting for them to join…
        <button class="link-btn" onClick={() => pull()}>
          Check now
        </button>
      </p>
      <button
        class="btn btn-ghost btn-wide"
        onClick={async () => {
          if (await ask("Leave matching?", { body: "Your swipes stay on this device and are removed from the page.", ok: "Leave", danger: true }))
            leaveRoom();
        }}
      >
        Leave
      </button>
    </div>
  );
}

function Waiting({ code }: { code: string }) {
  const link = `${siteOrigin()}/#join=${code}`;
  // shown and shared in two halves: ABCD 2345 (or ABC 234 for a code from before)
  const half = Math.ceil(code.length / 2);
  const share = () => {
    const spaced = `${code.slice(0, half)} ${code.slice(half)}`;
    const text =
      "Shall we choose the baby's name together? On Nameling we each swipe on our own phone, and it only shows " +
      `the names we both like. Tap to join me, or enter code ${spaced} under Together:`;
    return shareOrCopy({ title: "Choose a baby name with me", text, url: link }, `${text} ${link}`, "Invite link copied");
  };
  return (
    <div class="waiting">
      <h1 class="view-title center">Invite your partner</h1>
      <p class="view-sub center">Ask them to scan this, open your link, or enter the code under “I've got a pair code”.</p>
      <p class="muted small center">You can keep swiping while you wait. Names you've already liked count too.</p>
      <div class="code-card">
        <Qr text={link} label={`QR code linking to the invite for code ${code}`} />
        <p class="pair-code">
          <span aria-hidden="true">
            {code.slice(0, half)}
            <span>{code.slice(half)}</span>
          </span>
          {/* read out one character at a time */}
          <span class="sr-only">Pair code {code.split("").join(" ")}</span>
        </p>
      </div>
      <div class="stack">
        <button class="btn btn-primary btn-wide" onClick={share}>
          <IconShare size={18} /> Share invite link
        </button>
        <button
          class="btn btn-wide"
          onClick={() => navigator.clipboard?.writeText(code).then(() => toast("Code copied"), () => {})}
        >
          <IconCopy size={18} /> Copy code
        </button>
      </div>
      <p class="waiting-status">
        <span class="pulse" aria-hidden="true" /> Waiting for them to join…
        <button class="link-btn" onClick={() => pull()}>
          Check now
        </button>
      </p>
      <button
        class="btn btn-ghost btn-wide"
        onClick={async () => {
          if (await ask("Cancel this pair code?", { body: "Your partner won't be able to join with it.", ok: "Cancel code", danger: true }))
            leaveRoom().catch((e: Error) => toast(e.message));
        }}
      >
        Cancel pairing
      </button>
    </div>
  );
}

/**
 * Names close to what you've each liked: the names like your likes and like theirs (from each
 * name's `like` list), that neither of you has decided on yet. Best when it's close to both.
 */
function BothMightLike({ core }: { core: CoreData }) {
  const map = useStore(decisions, (s) => s.map);
  const sets = useStore(partnerSets, (s) => s);
  const [names, setNames] = useState<string[] | null>(null);
  const mine = useMemo(() => listByVerdict(map, [2, 1]).slice(0, 30), [map]);
  const theirs = useMemo(() => [...sets.loved, ...[...sets.liked].filter((k) => !sets.loved.has(k))].slice(0, 30), [sets]);

  useEffect(() => {
    if (mine.length < 2 || theirs.length < 2) return setNames([]);
    let live = true;
    const near = async (keys: string[]) => {
      const out = new Map<string, number>();
      const ds = await Promise.all(keys.map((k) => loadDetail(k).catch(() => null)));
      ds.forEach((d, i) =>
        likeList(core, d, keys[i]).forEach((k, j) => out.set(k, (out.get(k) ?? 0) + (1 - j / 16))),
      );
      return out;
    };
    Promise.all([near(mine), near(theirs)]).then(([a, b]) => {
      if (!live) return;
      const scored: [string, number][] = [];
      for (const [k, x] of a) {
        const y = b.get(k);
        // decided by you, or already liked or passed on by them: nothing to suggest
        if (!y || map[k] || sets.liked.has(k) || sets.nopes.has(k) || !core.byKey.has(k)) continue;
        scored.push([k, Math.sqrt(x * y)]);
      }
      scored.sort((p, q) => q[1] - p[1] || (p[0] < q[0] ? -1 : 1));
      setNames(scored.slice(0, 10).map(([k]) => k));
    });
    return () => {
      live = false;
    };
  }, [mine.join(","), theirs.join(","), core]);

  if (!names || names.length < 3) return null;
  const who = sets.others.map((m) => m.name).filter(Boolean).join(" & ") || "your partner";
  return (
    <section class="xsec both-might" aria-labelledby="both-might-title">
      <h2 class="xsec-title" id="both-might-title">
        Names you might both like
      </h2>
      <p class="muted small">
        Close in style to names you've liked and names {who} {sets.others.length > 1 ? "have" : "has"} liked. Neither of you has swiped on them yet.
      </p>
      <div class="chips">
        {names.map((k) => (
          <button key={k} class="chip-btn" onClick={() => openName(k)}>
            {entryFor(core, k).name}
          </button>
        ))}
      </div>
      <button
        class="btn btn-ghost btn-small like-swipe"
        onClick={() => swipeWith({ list: { title: "Names you might both like", keys: names, country: core.country.id } })}
      >
        Swipe these
      </button>
    </section>
  );
}

function Paired({ core }: { core: CoreData }) {
  useTick(30000);
  const p = useStore(partner, (s) => s);
  const sets = useStore(partnerSets, (s) => s);
  const map = useStore(decisions, (s) => s.map);
  const st = useStore(settings, (s) => s);
  const [showRestore, setShowRestore] = useState(false);
  const them = sets.others[0];
  const theirName = sets.others.map((m) => m.name || "Your partner").join(" & ");

  const matches = useMemo(() => {
    const keys = matchesOf(map, sets);
    const score = (k: string) => (map[k]?.d === 2 ? 1 : 0) + (sets.loved.has(k) ? 1 : 0);
    return keys
      .map((k) => entryFor(core, k, map[k]?.n))
      .sort((a, b) => score(b.key) - score(a.key) || (map[b.key]?.t ?? 0) - (map[a.key]?.t ?? 0));
  }, [map, sets, core]);

  // how often you agreed on names you've both decided (both yes, or both no)
  const agreement = useMemo(() => {
    if (!them) return null;
    const theirs = new Map<string, number>();
    them.nopes.forEach((k) => theirs.set(k, -1));
    them.likes.forEach((k) => theirs.set(k, 1));
    them.loves.forEach((k) => theirs.set(k, 2));
    let both = 0;
    let agree = 0;
    for (const [k, d] of Object.entries(map)) {
      const t = theirs.get(k);
      if (t === undefined) continue;
      both++;
      if ((d.d > 0) === (t > 0)) agree++;
    }
    return both >= 10 ? { both, pct: Math.round((agree / both) * 100) } : null;
  }, [map, them]);

  const theirUnseen = useMemo(() => [...sets.liked].filter((k) => !map[k] && core.byKey.has(k)).length, [sets, map, core]);
  const myLikes = useMemo(() => Object.values(map).filter((d) => d.d > 0).length, [map]);
  const theirLikes = them ? them.likes.length + them.loves.length : 0;

  return (
    <div class="paired">
      <div class="pair-head">
        <div class="pair-avatars" aria-hidden="true">
          <span class="avatar">{initialsOf(st.myName) || "Me"}</span>
          <span class="avatar avatar-2">{initialsOf(theirName) || "?"}</span>
        </div>
        <div>
          <h1 class="view-title">You & {theirName}</h1>
          <p class={`sync-line s-${p.status}`}>
            {p.status === "offline"
              ? "Offline: changes will sync when you're back"
              : p.status === "error"
                ? p.error
                : `Synced ${ago(p.lastSync)}`}
            <button class="icon-btn" onClick={() => pull()} aria-label="Sync now">
              <IconRefresh size={16} />
            </button>
          </p>
        </div>
      </div>

      <div class="tiles three">
        <div class="tile accent">
          <span class="tile-label">Matches</span>
          <b class="tile-value">{fmt(matches.length)}</b>
        </div>
        <div class="tile">
          <span class="tile-label">You like</span>
          <b class="tile-value">{fmt(myLikes)}</b>
        </div>
        <div class="tile">
          <span class="tile-label">{them?.name || "They"} like{them?.name ? "s" : ""}</span>
          <b class="tile-value">{fmt(theirLikes)}</b>
        </div>
      </div>

      {agreement && (
        <div class="agree" role="img" aria-label={`You agree on ${agreement.pct}% of the ${agreement.both} names you've both seen`}>
          <div class="agree-text">
            <b>{agreement.pct}%</b> agreement on the {fmt(agreement.both)} names you've both seen
          </div>
          <div class="agree-bar" aria-hidden="true">
            <span style={{ width: `${agreement.pct}%` }} />
          </div>
        </div>
      )}

      {theirUnseen > 0 && (
        <button
          class="cta-card"
          onClick={() => {
            filters.set({ ...DEFAULT_FILTERS, collection: "partner" });
            goTab("swipe");
          }}
        >
          <IconHeart size={22} filled />
          <span>
            <b>
              {fmt(theirUnseen)} name{theirUnseen === 1 ? "" : "s"} {theirName} liked
            </b>{" "}
            that you haven't seen yet. Swipe them now?
          </span>
        </button>
      )}

      <section class="xsec">
        <h2 class="xsec-title">Names you both like</h2>
        {matches.length === 0 ? (
          <p class="muted">No matches yet. They'll appear here (with a little fanfare) as soon as you agree on one.</p>
        ) : (
          <ul class="rows">
            {matches.map((e) => (
              <NameRow
                key={e.key}
                e={e}
                core={core}
                pref={st.sex}
                badge={
                  map[e.key]?.d === 2 && sets.loved.has(e.key) ? (
                    <span class="row-badge both-love">
                      <IconStar size={13} filled />
                      <IconStar size={13} filled />
                      <span class="sr-only">, you both love this</span>
                    </span>
                  ) : undefined
                }
              />
            ))}
          </ul>
        )}
      </section>

      <BothMightLike core={core} />

      <section class="xsec">
        <h2 class="xsec-title">How matching works</h2>
        <div class="toggles">
          <Toggle
            label={`Mix ${theirName}'s picks into my deck`}
            hint="Names they liked turn up sooner, so you find matches faster."
            checked={st.partnerFirst}
            onChange={(v) => settings.set({ partnerFirst: v })}
          />
          <Toggle
            label={`Skip names ${theirName} passed on`}
            hint="No point falling for a name they've already ruled out."
            checked={st.hidePartnerNopes}
            onChange={(v) => settings.set({ hidePartnerNopes: v })}
          />
          <Toggle
            label={`Show when ${theirName} likes a name`}
            hint="Off keeps your votes independent until you match."
            checked={st.partnerHints}
            onChange={(v) => settings.set({ partnerHints: v })}
          />
        </div>
      </section>

      {__PREVIEW__ ? (
        <p class="muted small">Your swipes are saved with this page, so they follow your Claude account to any device.</p>
      ) : (
      <section class="xsec">
        <button class="link-btn" onClick={() => setShowRestore(!showRestore)} aria-expanded={showRestore}>
          <IconLink size={16} /> Moving to a new phone?
        </button>
        {showRestore && (
          <div class="restore-box">
            <p class="muted small">
              Keep this restore code somewhere safe. Enter it in Settings on your new phone to bring back your swipes and
              this pairing. Anyone with it can act as you, so don't share it.
            </p>
            <code class="restore-code">{restoreCode()}</code>
            <button
              class="btn btn-small"
              onClick={() => navigator.clipboard?.writeText(restoreCode() ?? "").then(() => toast("Restore code copied"), () => {})}
            >
              <IconCopy size={16} /> Copy restore code
            </button>
          </div>
        )}
      </section>
      )}

      <button
        class="btn btn-danger-ghost btn-wide"
        onClick={async () => {
          if (
            await ask(__PREVIEW__ ? "Leave matching?" : `Unpair from ${theirName}?`, {
              body: __PREVIEW__
                ? "Your swipes stay on this device and are removed from the page, so you'll stop seeing each other's matches."
                : "This ends the pairing for both of you and deletes your names and lists from Nameling's server. Your own swipes stay on this phone.",
              ok: __PREVIEW__ ? "Leave" : "Unpair",
              danger: true,
            })
          )
            leaveRoom().catch((e: Error) => toast(e.message));
        }}
      >
        {__PREVIEW__ ? "Leave matching" : "Unpair"}
      </button>
    </div>
  );
}

export function TogetherView({ core }: { core: CoreData }) {
  const code = useStore(partner, (s) => s.code);
  const others = useStore(partnerSets, (s) => s.others.length);
  const members = useStore(partner, (s) => s.members.length);
  return (
    <section class="together-view" aria-label="Together">
      {!code ? (
        __PREVIEW__ ? (
          <PageNotPaired />
        ) : (
          <NotPaired />
        )
      ) : others === 0 && members < 2 ? (
        __PREVIEW__ ? (
          <PageWaiting />
        ) : (
          <Waiting code={code} />
        )
      ) : (
        <Paired core={core} />
      )}
    </section>
  );
}
